// src/providers/RecordQuerySyncProvider.tsx

import { ReactNode, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { recordMutationBus } from "shared/services/recordMutationBus";
import queryKeys, { isRelatedListQueryKey } from "shared/services/queryKeys";
import metadataQueryKeys from "shared/services/metadataQueryKeys";
import { themeQueryKey } from "./themeLogos";

interface RecordQuerySyncProviderProps {
    children: ReactNode;
}

export const RecordQuerySyncProvider = ({ children }: RecordQuerySyncProviderProps) => {
    const queryClient = useQueryClient();

    useEffect(() => {
        return recordMutationBus.subscribe(event => {
            const qKey = queryKeys.records.all(event.table);

            queryClient.invalidateQueries({
                queryKey: qKey,
                refetchType: "active",
            });

            if (event.table === "sys_users") {
                queryClient.invalidateQueries({ queryKey: queryKeys.session.me(), refetchType: "active" });
            }

            if (event.operation === "update") {
                // A parent may change the membership/display of related lists whose rows
                // belong to another table; their table prefix would not be invalidated above.
                // Related lists of the event table itself were already invalidated by the
                // prefix: invalidating them again cancels and repeats the in-flight fetch.
                queryClient.invalidateQueries({
                    predicate: query => isRelatedListQueryKey(query.queryKey) && query.queryKey[1] !== event.table,
                    refetchType: "active",
                });
            }

            if (event.table === "sys_themes") {
                queryClient.invalidateQueries({ queryKey: themeQueryKey, refetchType: "active" });
                queryClient.invalidateQueries({ queryKey: ["platform-theme-logos"], refetchType: "active" });
            }

            const metadataQueryKeysToRefresh =
                event.table === "sys_tables"
                    ? [metadataQueryKeys.tables]
                    : event.table === "sys_columns"
                        ? [metadataQueryKeys.columns, metadataQueryKeys.tables]
                        : [];

            metadataQueryKeysToRefresh.forEach(metadataQueryKey => {
                queryClient.invalidateQueries({
                    queryKey: metadataQueryKey,
                    refetchType: "active",
                    exact: true
                });
            });
        });
    }, [queryClient]);

    return (
        <>
            {children}
        </>
    );
};
